import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { BehaviorSignalBus } from '../contracts/signals';
import { BehaviorTrackerBase } from './BehaviorTrackerBase';

const ACTIVITY_EVENTS = [
  'mousemove',
  'scroll',
  'keydown',
  'touchstart',
] as const;

export type IdleTrackerOptions = {
  readonly signalBus?: BehaviorSignalBus;
};

export class IdleTracker extends BehaviorTrackerBase {
  private isIdle = false;

  private idleSince = 0;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly signalBus: BehaviorSignalBus | undefined;

  constructor(context: PixelRuntimeContext, options?: IdleTrackerOptions) {
    super(context);
    this.signalBus = options?.signalBus;
  }

  protected onInit(): void {
    if (!this.isWindowAvailable()) return;

    this.isIdle = false;
    this.idleSince = 0;
    this.scheduleIdleCheck();

    for (const eventName of ACTIVITY_EVENTS) {
      this.addWindowListener(eventName, () => this.onActivity(), {
        passive: true,
      });
    }
  }

  protected onDestroy(): void {
    this.idleTimer = this.clearManagedTimeout(this.idleTimer);
    this.isIdle = false;
    this.idleSince = 0;
  }

  private get thresholdMs(): number {
    return this.context.config.modules.behavior.trackers.idle.thresholdMs;
  }

  private scheduleIdleCheck(): void {
    this.idleTimer = this.clearManagedTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.enterIdle(), this.thresholdMs);
  }

  private onActivity(): void {
    const now = Date.now();

    this.signalBus?.publish(
      this.signalBus.createSignal('ACTIVITY', 'IdleTracker', { at: now })
    );

    if (this.isIdle) {
      const previousIdleDurationMs = Math.max(0, now - this.idleSince);
      this.isIdle = false;
      this.dispatchEngagement({
        type: 'USER_ACTIVE',
        category: 'ENGAGEMENT',
        activeAt: now,
        previousIdleDurationMs,
      });
    }

    this.scheduleIdleCheck();
  }

  private enterIdle(): void {
    if (this.isIdle) return;

    const now = Date.now();
    this.isIdle = true;
    this.idleSince = now;

    this.signalBus?.publish(
      this.signalBus.createSignal('IDLE', 'IdleTracker', {
        idleSince: now,
        thresholdMs: this.thresholdMs,
      })
    );

    this.dispatchEngagement({
      type: 'USER_IDLE',
      category: 'ENGAGEMENT',
      idleSince: now,
      thresholdMs: this.thresholdMs,
    });
  }
}
